import React, { useEffect, useRef, useState } from 'react';
import { geoMercator } from 'd3-geo';
import { scaleLinear } from 'd3-scale';

interface GeographicDistribution {
  country: string;
  countryName: string;
  patronCount: number;
  revenue: number;
  percentage: number;
  latitude: number | null;
  longitude: number | null;
}

interface WorldMapHeatmapProps {
  data: GeographicDistribution[];
  width?: number;
  height?: number;
  metric?: 'patronCount' | 'revenue';
}

/**
 * World map heatmap visualization for geographic patron distribution
 * Note: This is a simplified version. For production, use world.geo.json data
 */
export const WorldMapHeatmap: React.FC<WorldMapHeatmapProps> = ({
  data,
  width = 800,
  height = 500,
  metric = 'patronCount',
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredCountry, setHoveredCountry] = useState<GeographicDistribution | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    // Simple world map visualization using circles at coordinates
    // In production, you would load actual GeoJSON world data
    renderSimpleMap();
  }, [data, width, height, metric]);

  const renderSimpleMap = () => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clear existing content
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }

    // Create projection (Mercator)
    const projection = geoMercator()
      .scale(width / 6.5)
      .translate([width / 2, height / 1.5]);

    // Get max value for color scale
    const maxValue = Math.max(...data.map((d) => d[metric]));

    // Color scale (blue gradient)
    const colorScale = scaleLinear<string>()
      .domain([0, maxValue / 2, maxValue])
      .range(['#e0f2fe', '#0ea5e9', '#0c4a6e']);

    // Create group for circles
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    svg.appendChild(g);

    // Draw circles for each country with data
    data.forEach((country) => {
      if (!country.latitude || !country.longitude) return;

      const [x, y] = projection([country.longitude, country.latitude]) || [0, 0];

      // Circle size based on metric value
      const radius = Math.sqrt(country[metric]) * 0.5 + 3;

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', x.toString());
      circle.setAttribute('cy', y.toString());
      circle.setAttribute('r', radius.toString());
      circle.setAttribute('fill', colorScale(country[metric]));
      circle.setAttribute('stroke', '#0284c7');
      circle.setAttribute('stroke-width', '1');
      circle.setAttribute('opacity', '0.8');
      circle.style.cursor = 'pointer';

      // Add hover effect
      circle.addEventListener('mouseenter', (e) => {
        setHoveredCountry(country);
        setMousePos({ x: e.clientX, y: e.clientY });
        circle.setAttribute('opacity', '1');
        circle.setAttribute('stroke-width', '2');
      });

      circle.addEventListener('mousemove', (e) => {
        setMousePos({ x: e.clientX, y: e.clientY });
      });

      circle.addEventListener('mouseleave', () => {
        setHoveredCountry(null);
        circle.setAttribute('opacity', '0.8');
        circle.setAttribute('stroke-width', '1');
      });

      g.appendChild(circle);
    });
  };

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="border border-gray-200 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50"
      >
        {/* Background grid */}
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#grid)" />
      </svg>

      {/* Tooltip */}
      {hoveredCountry && (
        <div
          className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-xl pointer-events-none"
          style={{
            left: `${mousePos.x + 15}px`,
            top: `${mousePos.y - 15}px`,
          }}
        >
          <div className="font-semibold">{hoveredCountry.countryName}</div>
          <div className="text-gray-300 text-xs mt-1">
            <div>Patrons: {hoveredCountry.patronCount}</div>
            <div>Revenue: ${hoveredCountry.revenue.toFixed(2)}</div>
            <div>{hoveredCountry.percentage.toFixed(1)}% of total</div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white px-4 py-3 rounded-lg shadow-md border border-gray-200">
        <div className="text-xs font-semibold text-gray-700 mb-2">
          {metric === 'patronCount' ? 'Patron Count' : 'Revenue'}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#e0f2fe] border border-gray-300"></div>
            <span className="text-xs text-gray-600">Low</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#0ea5e9] border border-gray-300"></div>
            <span className="text-xs text-gray-600">Med</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#0c4a6e] border border-gray-300"></div>
            <span className="text-xs text-gray-600">High</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      {data.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg font-semibold mb-2">No geographic data available</p>
            <p className="text-sm">
              Patron geographic data will appear here when available
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMapHeatmap;
